import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLoan } from "../hooks/useLoan";
import {
  LOAN_STATUS, SLOT_STATUS_META, slotStatus, getSlotNumber, pad2,
  formatCountdown, slotImage,
} from "../lib/loans";

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
    green: "#16a34a", greenBg: "#f0fdf4", greenBorder: "#bbf7d0",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    orange: "#ea580c", orangeBg: "#fff7ed", orangeBorder: "#fed7aa",
    cardBg: "#ffffff", shadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#0f172a", bg2: "#131f30", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueBorder: "#1d4ed8",
    green: "#4ade80", greenBg: "#052e16", greenBorder: "#166634",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    orange: "#fb923c", orangeBg: "#2d1a00", orangeBorder: "#92400e",
    cardBg: "#1e293b", shadow: "0 1px 3px rgba(0,0,0,0.4)",
  },
};

const SLOT_COLLECTION = "calculator_slots";

// ── Station Grid ───────────────────────────────────────────
function StationGrid({ slots, highlightDocId, t }) {
  const n = Math.max(slots.length, 1);
  const cols = n <= 4 ? 2 : n <= 6 ? 3 : 2;

  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8, marginBottom: 16 }}>
      {slots.map(slot => {
        const num    = getSlotNumber(slot);
        const isHL   = highlightDocId === slot.docId;
        const status = slotStatus(slot);
        const meta   = SLOT_STATUS_META[status];
        const available = status === "available";

        const color = isHL ? "#2563eb" : meta.color;
        const label = isHL ? "Le vôtre" : meta.label;
        const img   = slotImage(slot);

        return (
          <div key={slot.docId} style={{
            background: t.cardBg, border: `2px solid ${color}`, borderRadius: 12,
            padding: "10px 8px", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 6, position: "relative",
            animation: isHL ? "labelBlink 1s ease infinite" : "none",
            boxShadow: isHL ? `0 0 16px ${color}44` : t.shadow,
          }}>
            <div style={{ position: "absolute", top: 6, left: 8, fontSize: 9, fontWeight: 700, color: t.text3, fontFamily: "monospace" }}>
              #{pad2(num)}
            </div>
            <div style={{ position: "absolute", top: 8, right: 8, width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}` }} />

            <div style={{ width: "100%", height: 70, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4 }}>
              {img ? (
                <img src={img} alt={slot.name || ""}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", opacity: available || isHL ? 1 : 0.35, filter: available || isHL ? "none" : "grayscale(60%)" }}
                  onError={e => { e.target.replaceWith(Object.assign(document.createElement("div"), { textContent: "🧮", style: "font-size:28px" })); }}
                />
              ) : (
                <div style={{ fontSize: 28, opacity: available || isHL ? 1 : 0.35 }}>🧮</div>
              )}
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: t.text1, textAlign: "center", fontFamily: "monospace", lineHeight: 1.2 }}>
              {slot.short || slot.name?.split(" ").slice(-1)[0] || `#${pad2(num)}`}
            </div>
            <div style={{ fontSize: 9, fontWeight: 600, color, background: `${color}14`, border: `1px solid ${color}`, borderRadius: 20, padding: "2px 8px" }}>
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReturnArrows() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setStep(s => (s + 1) % 3), 500);
    return () => clearInterval(i);
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "8px auto 0" }}>
      {[0, 1, 2].map(i => {
        const lit = i <= step;
        return (
          <svg key={i} width="40" height="24" viewBox="0 0 40 24" fill="none" style={{ display: "block", marginTop: i === 0 ? 0 : -6 }}>
            <path d="M4 4L20 20L36 4" stroke={lit ? "#2563eb" : "rgba(37,99,235,0.18)"} strokeWidth={lit ? "4" : "2.5"} strokeLinecap="round" strokeLinejoin="round" style={{ transition: "stroke 0.15s, stroke-width 0.15s" }} />
          </svg>
        );
      })}
    </div>
  );
}

export default function CalculatorPage() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [returning, setReturning] = useState(false);
  const t = dark ? theme.dark : theme.light;

  const {
    user, ready, slots, availableSlots, loan, loanSlot, loanMinutes,
    secondsLeft, totalSeconds, overdue, busy, error, clearError, justClosed,
    borrow, reportCondition, declareReturn,
  } = useLoan(SLOT_COLLECTION, { fallbackMinutes: 60 });

  // Remet l'étape locale à zéro quand l'emprunt change d'état
  useEffect(() => { setReturning(false); }, [loan?.id, loan?.status]);

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  const slotDisplay = pad2(loan?.slotNumber ?? getSlotNumber(loanSlot));
  const loanName    = loan?.slotName || loanSlot?.name;
  const loanImg     = slotImage(loanSlot);
  const availableCount = availableSlots.length;

  // ── Écran courant (dérivé de Firestore, survit au rechargement) ──
  let view;
  if (!ready) view = "loading";
  else if (!user) view = "login";
  else if (justClosed && !loan) view = "done";
  else if (!loan) view = error === "none" ? "none" : "idle";
  else if (loan.status === LOAN_STATUS.RETURN_PENDING) view = "pending";
  else if (returning) view = "returning";
  else if (!loan.condition) view = "check";
  else view = overdue ? "overdue" : "countdown";

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = totalSeconds ? (secondsLeft ?? 0) / totalSeconds : 1;
  const strokeDashoffset = circumference * (1 - progress);
  const isUrgent = secondsLeft !== null && secondsLeft < 300;

  const btn = (bg, color = "#fff", extra = {}) => ({
    width: "100%", border: "none", borderRadius: 14, background: bg, color,
    fontSize: 15, fontWeight: 700, padding: 15, cursor: busy ? "wait" : "pointer",
    opacity: busy ? 0.7 : 1, ...extra,
  });

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn    { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop       { 0%{transform:scale(0.85);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes shake     { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)} 75%{transform:translateX(6px)} }
        @keyframes spin      { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes labelBlink{ 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>

      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/equipment")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60 }}>

          {view === "loading" && (
            <div style={{ textAlign: "center", color: t.text3, fontSize: 13 }}>Chargement…</div>
          )}

          {view === "login" && (
            <div style={{ textAlign: "center", color: t.text2, fontSize: 14 }}>Connectez-vous pour emprunter une calculatrice.</div>
          )}

          {/* ── IDLE ── */}
          {view === "idle" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <StationGrid slots={slots} highlightDocId={null} t={t} />
              <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Station Calculatrices</div>
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 20, lineHeight: 1.6 }}>
                {slots.length === 0 ? "Aucune calculatrice configurée"
                  : availableCount > 0 ? `${availableCount} calculatrice${availableCount > 1 ? "s" : ""} disponible${availableCount > 1 ? "s" : ""}`
                  : "Aucune calculatrice disponible"}
              </div>
              {error && error !== "none" && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
              <button onClick={borrow} disabled={busy || availableCount === 0}
                style={btn(availableCount > 0 ? t.blue : t.bg3, availableCount > 0 ? "#fff" : t.text3, { fontSize: 16, padding: 16, cursor: availableCount > 0 ? "pointer" : "not-allowed", opacity: availableCount > 0 ? 1 : 0.6 })}>
                🧮 Emprunter une calculatrice
              </button>
              <div style={{ fontSize: 11, color: t.text3, marginTop: 14 }}>
                {loanMinutes ? `Durée de prêt : ${loanMinutes} minutes • ` : ""}Retour validé par l'accueil
              </div>
            </div>
          )}

          {/* ── CHECK : récupération + état ── */}
          {view === "check" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <StationGrid slots={slots} highlightDocId={loan.slotDocId} t={t} />
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Prenez la calculatrice</div>
              <div style={{ width: 120, height: 120, borderRadius: "50%", background: t.blueBg, border: `3px solid ${t.blue}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", animation: "pop 0.4s ease", boxShadow: `0 0 32px ${t.blue}44` }}>
                <span style={{ fontSize: 54, fontWeight: 900, color: t.blue, lineHeight: 1 }}>{slotDisplay}</span>
              </div>
              {loanName && <div style={{ fontSize: 13, fontWeight: 600, color: t.text1, marginBottom: 8 }}>{loanName}</div>}
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 8, lineHeight: 1.6 }}>Récupérez la calculatrice de l'emplacement <strong style={{ color: t.text1 }}>{slotDisplay}</strong></div>
              {loanMinutes && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.orangeBg, border: `1px solid ${t.orangeBorder}`, borderRadius: 20, padding: "4px 12px", marginBottom: 20 }}>
                  <span style={{ fontSize: 12, color: t.orange, fontWeight: 600 }}>⏱ Vous avez {loanMinutes} min pour la rendre</span>
                </div>
              )}
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 12 }}>Elle fonctionne bien ?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button disabled={busy} onClick={() => reportCondition("good")} style={btn(t.green, "#fff", { fontSize: 14, padding: 14 })}>✅ Elle fonctionne bien</button>
                <button disabled={busy} onClick={() => reportCondition("defective")} style={btn(t.redBg, t.red, { fontSize: 14, padding: 14, border: `1.5px solid ${t.redBorder}` })}>⚠️ Elle est défectueuse</button>
              </div>
              <div style={{ fontSize: 11, color: t.text3, marginTop: 12 }}>Un défaut signalé maintenant ne vous sera pas reproché.</div>
            </div>
          )}

          {/* ── COUNTDOWN ── */}
          {view === "countdown" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
                {secondsLeft !== null ? "Temps restant" : "Emprunt en cours"}
              </div>
              {secondsLeft !== null && (
                <div style={{ position: "relative", width: 160, height: 160, margin: "0 auto 24px" }}>
                  <svg width="160" height="160" style={{ transform: "rotate(-90deg)" }}>
                    <circle cx="80" cy="80" r={radius} fill="none" stroke={t.bg3} strokeWidth="8" />
                    <circle cx="80" cy="80" r={radius} fill="none" stroke={isUrgent ? t.red : t.blue} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{ transition: "stroke-dashoffset 1s linear, stroke 0.5s" }} />
                  </svg>
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontSize: 28, fontWeight: 900, color: isUrgent ? t.red : t.text1, fontVariantNumeric: "tabular-nums", animation: isUrgent ? "pulse 1s infinite" : "none" }}>{formatCountdown(secondsLeft)}</div>
                    <div style={{ fontSize: 10, color: t.text3, marginTop: 2 }}>min:sec</div>
                  </div>
                </div>
              )}
              {loanName && <div style={{ fontSize: 13, fontWeight: 600, color: t.text1, marginBottom: 4 }}>{loanName}</div>}
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 28 }}>Remettez-la à l'emplacement <strong style={{ color: t.text1 }}>{slotDisplay}</strong> quand vous avez terminé</div>
              <button onClick={() => setReturning(true)} style={btn(t.blue)}>🧮 Rendre la calculatrice</button>
            </div>
          )}

          {/* ── OVERDUE ── */}
          {view === "overdue" && (
            <div style={{ animation: "shake 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.redBg, border: `2px solid ${t.red}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>⚠️</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.red, marginBottom: 8 }}>Temps écoulé !</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 28, lineHeight: 1.6 }}>Veuillez remettre la calculatrice à l'emplacement <strong style={{ color: t.text1 }}>{slotDisplay}</strong> immédiatement.</div>
              <button onClick={() => setReturning(true)} style={btn(t.red)}>🧮 Rendre la calculatrice</button>
            </div>
          )}

          {/* ── RETURNING ── */}
          {view === "returning" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 20 }}>Remise en place</div>
              {loanImg && (
                <div style={{ marginBottom: 8 }}>
                  <img src={loanImg} alt={loanName} style={{ height: 120, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
                </div>
              )}
              <ReturnArrows />
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 6, marginTop: 20 }}>Emplacement <span style={{ color: t.blue }}>{slotDisplay}</span></div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Reposez la calculatrice à sa place, puis confirmez.<br />L'accueil validera le retour.</div>
              {error && <div style={{ fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button disabled={busy} onClick={() => { clearError(); declareReturn(); }} style={btn(t.green)}>✅ Calculatrice remise en place</button>
                <button onClick={() => setReturning(false)} style={btn(t.bg3, t.text2, { fontSize: 13, padding: 11 })}>Annuler</button>
              </div>
            </div>
          )}

          {/* ── PENDING : en attente de validation accueil ── */}
          {view === "pending" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 28px" }}>
                <svg width="100" height="100" style={{ animation: "spin 1.6s linear infinite" }}>
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.bg3} strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.orange} strokeWidth="6" strokeLinecap="round" strokeDasharray="70 194" />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🧮</div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Retour en attente de validation</div>
              <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>L'accueil va vérifier la calculatrice de l'emplacement <strong style={{ color: t.text1 }}>{slotDisplay}</strong>.<br />Vous pouvez quitter cette page.</div>
            </div>
          )}

          {/* ── DONE ── */}
          {view === "done" && (
            <div style={{ animation: "pop 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Merci !</div>
              <div style={{ fontSize: 13, color: t.text2 }}>{justClosed === "returned" ? "Retour validé par l'accueil." : "Votre emprunt a été clôturé."}</div>
            </div>
          )}

          {/* ── NONE ── */}
          {view === "none" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Aucune calculatrice disponible</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32 }}>Quelqu'un vient de prendre la dernière.</div>
              <button onClick={clearError} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}